import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Folder, ChevronLeft, Clock } from 'react-feather';
import { toast } from 'react-hot-toast';
import api from '../services/api.ts';

export interface Document {
  id: string;
  title: string;
  content: string;
  created_at: string;
  topic: string;
  // We'll use "folder" for our artificial folders and "notion" for leaf pages.
  type: string; 
  url?: string;
  parentId?: string;
  children?: Document[];
}

export interface DocumentBatch {
  id: string;
  timestamp: string;
  documentCount: number;
  topics: string[];
  documents: Document[];
  source?: 'generated' | 'notion';
}

export interface NextUpdateInfo {
  nextUpdateTime: string;
  inProgress: boolean;
}

interface ApiResponse<T> {
  documents: T[];
  error?: string;
}

interface NotionDocument {
  id: string;
  title: string;
  created_at: string;
  topic: string;
  url?: string;
}

// ─── FETCHING BATCHES (unchanged) ─────────────────────────────────────────────
const fetchDocumentBatches = async (): Promise<DocumentBatch[]> => {
  const batches: DocumentBatch[] = [];
  try {
    const resultsResponse = await api.get('/demo/results');
    if (resultsResponse.data && resultsResponse.data.summaries) {
      // Create a map of topic_id to title for easier lookup
      const topicMap = (resultsResponse.data.topics || [])
        .filter((t: any) => t && t.title && t.id)
        .reduce((acc: {[key: string]: string}, t: any) => {
          acc[t.id] = t.title;
          return acc;
        }, {});
        
      const generatedBatch: DocumentBatch = {
        id: `generated-${new Date().toISOString()}`,
        timestamp: resultsResponse.data.last_update || new Date().toISOString(),
        documentCount: resultsResponse.data.summaries.length,
        topics: Object.values(topicMap),
        documents: resultsResponse.data.summaries.map((s: any) => ({
          id: s.topic_id || Math.random().toString(),
          title: topicMap[s.topic_id] || s.title || 'Generated Summary',
          content: s.summary || '',
          created_at: new Date().toISOString(),
          topic: 'Generated',
          type: 'notion',
          url: s.notion_url || s.url || null,
        })),
        source: 'generated',
      };
      if (generatedBatch.documents.length > 0) {
        batches.push(generatedBatch);
      }
    }
  } catch (error) {
    console.error('Error fetching generated documents:', error);
    toast.error('Failed to fetch generated documents');
  }
  try {
    const notionResponse = await api.get('/notion/activity');
    if (notionResponse.data && Array.isArray(notionResponse.data)) {
      const notionDocs = notionResponse.data;
      // const knowledgeBaseDocs = notionDocs.filter((doc: any) => {
      //   return doc?.details?.title &&
      //     !doc.details.title.startsWith('Summary: ') &&
      //     !doc.details.title.startsWith('Generated Batch - ');
      // });
      // if (knowledgeBaseDocs.length > 0) {
      //   const knowledgeBaseBatch: DocumentBatch = {
      //     id: `knowledge-base-${new Date().toISOString()}`,
      //     timestamp: new Date().toISOString(),
      //     documentCount: knowledgeBaseDocs.length,
      //     topics: ['Knowledge Base'],
      //     documents: knowledgeBaseDocs.map((doc: any) => ({
      //       id: doc.details.id || Math.random().toString(),
      //       title: doc.details.title,
      //       content: doc.details.content || '',
      //       created_at: doc.timestamp || new Date().toISOString(),
      //       type: 'notion',
      //       topic: 'Knowledge Base',
      //       url: doc.details.url,
      //     })),
      //     source: 'notion',
      //   };
      //   batches.push(knowledgeBaseBatch);
      // }
      const generatedDocs = notionDocs.filter((doc: any) =>
        doc?.details?.title &&
        (doc.details.title.startsWith('Summary: ') ||
         doc.details.title.startsWith('Generated Batch - '))
      );
      if (generatedDocs.length > 0) {
        const groups = generatedDocs.reduce((acc, doc: any) => {
          const batchId = doc.details.batch_id || doc.details.timestamp;
          if (!acc[batchId]) {
            acc[batchId] = [];
          }
          acc[batchId].push({
            id: doc.details.id || Math.random().toString(),
            title: doc.details.title,
            content: doc.details.content || '',
            created_at: doc.timestamp || new Date().toISOString(),
            type: 'notion',
            topic: 'Generated',
            url: doc.details.url,
          });
          return acc;
        }, {} as Record<string, Document[]>);
        for (const [batchId, docs] of Object.entries(groups)) {
          const generatedBatch: DocumentBatch = {
            id: `generated-notion-${batchId}`,
            timestamp: docs[0].created_at,
            documentCount: docs.length,
            topics: [`Generated Summaries ${batchId}`],
            documents: docs,
            source: 'notion',
          };
          batches.push(generatedBatch);
        }
      }
    } else {
      console.log('Invalid or empty Notion response:', notionResponse.data);
    }
  } catch (error) {
    console.error('Error fetching Notion documents:', error);
    toast.error('Failed to fetch Notion documents');
  }
  if (batches.length === 0) {
    toast('No documents found', {
      icon: 'ℹ️',
    });
  }
  return batches;
};

// ─── BUILD THE TREE ────────────────────────────────────────────────────────────
// Here we build a hierarchical tree:
//  • Root folder: "CASPR DOCS"
//    └─ Employee folder: "CASPR - John Doe"
//         └─ Batch folders (one per batch)
//              └─ Topic folders (grouping docs by topic)
//                   └─ Notion pages (clickable links)
const buildEmployeeDocsTree = (employeeName: string, batches: DocumentBatch[]): Document => {
  const root: Document = {
    id: 'root',
    title: 'CASPR DOCS',
    content: '',
    created_at: new Date().toISOString(),
    topic: '',
    type: 'folder',
    children: [],
  };

  const employeeFolder: Document = {
    id: `employee-${employeeName}`,
    title: `CASPR - ${employeeName}`,
    content: '',
    created_at: new Date().toISOString(),
    topic: '',
    type: 'folder',
    children: [],
  };

  root.children!.push(employeeFolder);

  // Sort batches by timestamp, newest first
  const sortedBatches = [...batches].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  sortedBatches.forEach(batch => {
    const batchFolder: Document = {
      id: `batch-${batch.id}`,
      title: `Batch from ${format(new Date(batch.timestamp), 'MMM d, yyyy h:mm a')} (${batch.documentCount} docs)`,
      content: '',
      created_at: batch.timestamp,
      topic: '',
      type: 'folder',
      children: batch.documents.map(doc => ({
        ...doc,
        type: 'notion',
      })),
    };

    employeeFolder.children!.push(batchFolder);
  });

  return root;
};

// ─── FOLDER ITEM COMPONENT ─────────────────────────────────────────────────────
// This component displays one folder (or file) as a clickable card.
// If the item is a folder, clicking it will "drill down" (via our navigation stack).
// If it's a notion page (leaf) with a URL, clicking it opens the page.
const FolderItem: React.FC<{ item: Document; onFolderClick: (item: Document) => void }> = ({ item, onFolderClick }) => {
  const handleClick = () => {
    if (item.type === 'folder') {
      onFolderClick(item);
    } else if (item.type === 'notion' && item.url) {
      try {
        window.open(item.url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Error opening Notion URL:', error);
        toast.error('Unable to open Notion page');
      }
    }
  };

  return (
    <div
      onClick={handleClick}
      className="p-2 bg-white rounded-lg border border-gray-200 hover:border-blue-500 cursor-pointer transition-colors flex items-center"
    >
      {item.type === 'folder' ? (
        <Folder className="w-5 h-5 text-blue-500 flex-shrink-0" />
      ) : (
        <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
      )}
      <span className="ml-2 text-sm font-medium text-gray-900">{item.title}</span>
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────────
export default function DocumentsView() {
  const [generating, setGenerating] = useState(false);
  const [nextUpdate, setNextUpdate] = useState<NextUpdateInfo | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Fetch GitHub user data including provider name
  const { data: githubData } = useQuery({
    queryKey: ['github-status'],
    queryFn: async () => {
      const response = await fetch('https://api.usepeppr.ai/api/github/status', { 
        credentials: 'include' 
      });
      if (!response.ok) throw new Error('Failed to fetch GitHub status');
      return response.json();
    }
  });

  // Fetch document batches
  const { data: documents, isLoading, error } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      try {
        const response = await api.get<ApiResponse<NotionDocument>>('/notion/documents');
        console.log('Notion documents response:', response.data); // Debug log
        return response.data?.documents || [];
      } catch (error) {
        console.error('Error fetching documents:', error);
        throw error;
      }
    }
  });

  const fetchNextUpdateTime = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setUTCHours(24, 0, 0, 0);
    setNextUpdate({ nextUpdateTime: tomorrow.toISOString(), inProgress: false });
  };

  const updateTimeRemaining = () => {
    if (!nextUpdate?.nextUpdateTime) return;
    const now = new Date();
    const next = new Date(nextUpdate.nextUpdateTime);
    const diff = next.getTime() - now.getTime();
    if (diff <= 0) {
      setTimeRemaining('Updating soon...');
      fetchNextUpdateTime();
      return;
    }
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
  };

  useEffect(() => {
    fetchNextUpdateTime();
    const timer = setInterval(updateTimeRemaining, 1000);
    return () => clearInterval(timer);
  }, []);

  // ─── GENERATE NEW BATCH (unchanged) ───────────────────────────────
  const generateNewBatch = async () => {
    try {
      setGenerating(true);
      // Reserve a CASPR batch page.
      const batchPayload = {
        employee_name: githubData?.user?.provider_name || 'Unknown Employee',
        batch_data: { topics: [] },
      };
      const casprBatchResponse = await api.post('/notion/create-caspr-batch', batchPayload);
      const batch_id = casprBatchResponse.data.batch_page_id;
      toast.success(`Batch page created (ID: ${batch_id})`);
      
      // Start document generation.
      const startResponse = await api.post('/demo/start', { batch_id });
      if (startResponse.data.status === 'success') {
        toast.success('Document generation started');
        // Poll for completion.
        const checkStatus = setInterval(async () => {
          const statusResponse = await api.get('/demo/status', { params: { batch_id } });
          if (!statusResponse.data.is_running && statusResponse.data.current_step === 'Complete') {
            clearInterval(checkStatus);
            // Fetch demo results.
            const resultsResponse = await api.get('/demo/results', { params: { batch_id } });
            if (resultsResponse.data && resultsResponse.data.summaries) {
              const topics = (resultsResponse.data.topics || [])
                .filter((t: any) => t && t.title)
                .map((t: any) => t.title);
              // Update the batch page with the real topics.
              const updatePayload = {
                employee_name: githubData?.user?.provider_name || 'Unknown Employee',
                batch_data: { topics },
              };
              await api.post('/notion/update-caspr-batch', { batch_id, ...updatePayload });
              toast.success('New batch updated successfully in Notion!');
            } else {
              toast.error('No demo results available');
            }
            setGenerating(false);
          } else if (statusResponse.data.error) {
            clearInterval(checkStatus);
            toast.error(`Generation failed: ${statusResponse.data.error}`);
            setGenerating(false);
          }
        }, 5000);
      } else {
        toast.error('Failed to start document generation');
        setGenerating(false);
      }
    } catch (error) {
      console.error('Error generating new batch:', error);
      toast.error('Failed to generate new batch');
      setGenerating(false);
    }
  };

  // ─── BUILD THE TREE FOR OUR NAVIGATION ─────────────────────────────
  // If there are batches, build the full tree; otherwise, create an empty root.
  const tree = useMemo(() => {
    const root: Document = {
      id: 'root',
      title: 'CASPR DOCS',
      content: '',
      created_at: new Date().toISOString(),
      topic: '',
      type: 'folder',
      children: documents || [],
    };
    return root;
  }, [documents]);

  // We'll use a navigation stack so that clicking a folder "drills in."
  // The top (last) item in the stack is the current folder.
  const [navStack, setNavStack] = useState<Document[]>([]);
  useEffect(() => {
    if (tree) {
      setNavStack([tree]);
    }
  }, [tree]);

  const currentFolder = navStack[navStack.length - 1] || tree;

  const handleFolderClick = (item: Document) => {
    if (item.type === 'folder' && item.children && item.children.length > 0) {
      setNavStack(prev => [...prev, item]);
    } else if (item.type === 'folder') {
      toast('No items in this folder', {
        icon: 'ℹ️',
      });
    }
  };

  const handleBack = () => {
    if (navStack.length > 1) {
      setNavStack(prev => prev.slice(0, prev.length - 1));
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="text-red-500 p-4">
        Error loading documents. Please try again later.
      </div>
    );
  }

  // Show empty state
  if (!documents || documents.length === 0) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">CASPR DOCS</h1>
        {/* <div className="flex items-center space-x-2 text-gray-500">
          <Clock className="w-4 h-4" />
          <span>Next update at: {format(nextUpdate?.nextUpdateTime || new Date(), 'h:mm a UTC')}</span>
        </div> */}
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">No items found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with next update info and generate button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">CASPR DOCS</h2>
          <div className="flex items-center mt-2 text-sm">
            <Clock className="w-4 h-4 mr-2 text-blue-500" />
            <span className="font-medium text-gray-700">Next update at:</span>
            <span className="ml-2 text-blue-600 font-semibold">
              {nextUpdate?.nextUpdateTime
                ? format(new Date(nextUpdate.nextUpdateTime), 'h:mm a') + ' UTC'
                : 'Calculating...'}
            </span>
            {timeRemaining && <span className="ml-2 text-gray-600">{timeRemaining}</span>}
          </div>
        </div>
        <button
          onClick={generateNewBatch}
          disabled={generating}
          className={`px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium ${
            generating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
          }`}
        >
          {generating ? 'Generating...' : 'Generate New Batch'}
        </button>
      </div>

      {/* Back button (if not at the root) */}
      {navStack.length > 1 && (
        <button
          onClick={handleBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )}

      {/* Display the current folder's children */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentFolder?.children?.length ? (
          currentFolder.children.map(item => (
            <FolderItem key={item.id} item={item} onFolderClick={handleFolderClick} />
          ))
        ) : (
          <p className="text-gray-600">No items found.</p>
        )}
      </div>
    </div>
  );
}
